import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, Alert, Spin, Button } from "antd";
import { supabase } from "../lib/supabase";
import { generateAndSaveShippingLabel } from "../utils/shippingLabel";
import { useTranslation } from "react-i18next";

export const RegenerateLabelPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsAuth, setNeedsAuth] = useState(false);

  useEffect(() => {
    const regenerateLabel = async () => {
      try {
        // Check authentication first
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) {
          // Store the current URL in localStorage to redirect back after login
          const currentUrl = window.location.href;
          localStorage.setItem("redirectAfterAuth", currentUrl);
          setNeedsAuth(true);
          setLoading(false);
          return;
        }

        // Get all parameters
        const productName = searchParams.get("productName");
        const pieces = searchParams.get("pieces");
        const cartons = searchParams.get("cartons");
        const weight = searchParams.get("weight");
        const cbm = searchParams.get("cbm");
        const ean = searchParams.get("ean");
        const bookingId = searchParams.get("bookingId");
        const productId = searchParams.get("productId");
        const companyId = searchParams.get("companyId");
        const companyName = searchParams.get("companyName");
        const transportType = searchParams.get("transportType") || ""; // AIR or SEA

        // Verify all required parameters are present
        if (
          !productName ||
          !pieces ||
          !cartons ||
          !weight ||
          !cbm ||
          !ean ||
          !bookingId ||
          !productId ||
          !companyId ||
          !companyName
        ) {
          throw new Error(t("shippingLabel.noData"));
        }

        // Generate the shipping label
        const labelPath = await generateAndSaveShippingLabel(
          {
            id: parseInt(bookingId),
            reference: transportType, // Use transport type as reference
            transport_type: {
              name: transportType === "AIR" ? "Air Freight" : "Sea Freight",
              abbreviation: transportType,
            },
            booking_incoterm: { name: "" }, // Not needed for regeneration
            supplier: {
              name: "",
              email: "",
              phone: "",
              country: "",
              city: "",
              address: "",
            },
          },
          {
            id: parseInt(productId),
            name: productName,
            hs_code: null,
            gross_weight: Number(parseFloat(weight).toFixed(2)),
            cbm: Number(parseFloat(cbm).toFixed(2)),
            pieces: parseInt(pieces),
            cartons: parseInt(cartons),
            ean: parseInt(ean),
            company_id: parseInt(companyId),
          },
          companyName
        );

        if (!labelPath) {
          throw new Error(t("shippingLabel.regenerateError"));
        }

        // Get the download URL
        const { data: urlData } = await supabase.storage
          .from("shipping_labels")
          .createSignedUrl(labelPath, 3600);

        if (urlData?.signedUrl) {
          // Create a temporary link and trigger download
          const link = document.createElement("a");
          link.href = urlData.signedUrl;
          link.download = `booking_${bookingId}_product_${productId}_${transportType}_shipping_label.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } catch (err) {
        console.error("Error regenerating label:", err);
        setError(
          err instanceof Error
            ? err.message
            : t("shippingLabel.regenerateError")
        );
      } finally {
        setLoading(false);
      }
    };

    regenerateLabel();
  }, [searchParams, t, navigate]);

  if (needsAuth) {
    return (
      <Card style={{ maxWidth: 600, margin: "50px auto", textAlign: "center" }}>
        <Alert
          message={t("shippingLabel.authRequired")}
          type="info"
          showIcon
          style={{ marginBottom: "20px" }}
        />
        <Button type="primary" onClick={() => navigate("/auth")}>
          {t("common.login")}
        </Button>
      </Card>
    );
  }

  if (!loading && error) {
    return (
      <Card style={{ maxWidth: 600, margin: "50px auto" }}>
        <Alert message={error} type="error" showIcon />
      </Card>
    );
  }

  return (
    <div style={{ textAlign: "center", padding: "50px" }}>
      <Spin size="large" />
      <p style={{ marginTop: "20px" }}>
        {t("shippingLabel.regeneratingLabel")}
      </p>
    </div>
  );
};
